
import Component from 'vue-class-component';
import SecondaryLineBarChartFilterable from '@/components/reports-v3/components/filterables/SecondaryLineBarChartFilterable.vue';
import FilterWidget from '@/components/reports-v3/components/codedWidgets/shared/FilterWidget.vue';
import echarts from 'echarts';
import moment from 'moment/moment';
import gdbx from '@/store/modules/gdbx';
import {DataViewFormatter} from '@/components/reports-v3/components/elements/charts/helpers/dataViewFormatter';
import {GobiColor} from '@/helpers/color';
import SecondaryLineBarChart from '@/components/reports-v3/components/elements/charts/SecondaryLineBarChart.vue';
import TableFilterable from '@/components/reports-v3/components/filterables/TableFilterable.vue';
import {TableItemFormatter} from '@/components/reports-v3/components/elements/charts/helpers/tableItemFormatter';
import {Moment, MomentInput} from 'moment';
import { getTableName, TIMEZONE_OFFSET_MS } from '@/worker/db/sqlHelpers';

@Component({
  components: {
    TableFilterable,
    SecondaryLineBarChart,
    SecondaryLineBarChartFilterable,
  },
})
export default class TiarcoChemicalMYearlyComparison extends FilterWidget {

  public get currencySymbol() {
    return gdbx.currencySymbol;
  }

  public get decimal() {
    return gdbx.numDecimal;
  }

    public get currentYearRange(): [number, number] {
        const dateFormat = 'DD/MM/YYYY';

        const accountingMonth = moment(this.accountingDate, dateFormat).month();
        const selectedDate = moment(this.selectedAsOfDate);
        const selectedYear = selectedDate.year();
        const selectedMonth = selectedDate.month();

        const startYear = selectedMonth < accountingMonth ? selectedYear - 1 : selectedYear;

        const startDate = moment()
            .year(startYear)
            .month(accountingMonth)
            .startOf('month')
            .valueOf();

        const endDate = moment(this.selectedAsOfDate).endOf('month').valueOf();
        return [startDate, endDate];
    }

  public get dataViewFormatter() {
    return DataViewFormatter.salesTracker;
  }

  public get expensiveHook() {
    const {
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
        selectedProjects,
    } = this;
    return JSON.stringify([
      selectedDateRange,
      selectedAsOfDate,
      currentYearRange,
      selectedProjects,
    ]);
  }

  public get monthValuesCurrent() {
    const [d0, d1] = this.currentYearRange.map((d) => moment(d));
    const period = d1.diff(d0, 'month') + 1;
    const result: Moment[] = [];
    for (let i = 0; i < period; i++) {
      result.push(d0.clone());
      d0.add(1, 'month');
    }
    return result;
  }

  public filterIds: Array<
      | 'date'
      | 'month'
      | 'dateAsOf'
      | 'dateRange'
      | 'stockItems'
      | 'agents'
      | 'customers'
      | 'suppliers'
      | 'project'
  > = ['suppliers', 'agents', 'dateAsOf', 'month', 'project'];

  public xAxisData: string[] = [];
  public series: echarts.ECharts[] | any[] = [];

  public tableFields: object[] = [];

  public tableItems: object[] = [];
  public accountingDate: string = '';

  public exportName(title: string, range: number[]) {
    return title + ' from ' +
        moment(range[0] as MomentInput).format('DD MMM YY') +
        ' - ' +
        moment(range[1] as MomentInput).format('DD MMM YY');
  }

  public async expensiveCalc() {
    const escapeStr = (s: string) => s.replace(/'/g, '\'\'');
    const table = getTableName('transactions');
    const systemTable = getTableName('system');
    const amt = 'TRY_CAST("AMOUNT" AS DOUBLE)';
    const dateExpr = 'TRY_CAST("DOCDATE" AS BIGINT)';
    const yearExpr = `EXTRACT(YEAR FROM EPOCH_MS(`
        + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;
    const monthExpr = `EXTRACT(MONTH FROM EPOCH_MS(`
        + `${dateExpr} + ${TIMEZONE_OFFSET_MS}))`;

    let projectFilter = '';
    if (this.selectedProjects.length > 0) {
      const list = this.selectedProjects
          .map((p) => `'${escapeStr(p)}'`).join(',');
      projectFilter = ` AND "PROJECT" IN (${list})`;
    }

    const jobId = this.currentJobId;
    const queryHead = await this._ensureTablesLoaded(
        ['transactions', 'system', 'stockBalances'],
    );
    this.checkInterrupted(jobId);

    // Load accounting date
    const systemResult = await queryHead.rawQuery(
        `SELECT "VAL" FROM ${systemTable} LIMIT 1`,
    );
    this.accountingDate = systemResult[0] && systemResult[0].VAL || '';

    if (this.accountingDate) {
        const range = this.currentYearRange;

        this.generateLoadingText(0.3);

        // Single cross-tab query for all 6 accTypes + stock movements
        const [crossResult, stockMovements] = await Promise.all([
            queryHead.rawQuery(`
                SELECT "ACCTYPE" as accType,
                    ${yearExpr} as year,
                    ${monthExpr} as month,
                    SUM(${amt}) as total
                FROM ${table}
                WHERE ${dateExpr}
                    BETWEEN ${range[0]} AND ${range[1]}
                    AND "ACCTYPE" IN ('SL','SA','CO','EP','OI','EO')
                    ${projectFilter}
                GROUP BY accType, year, month
            `),
            Promise.all(
                this.monthValuesCurrent.map(async (m) => {
                    return await this._getStockMovement(queryHead, [
                        m.valueOf(),
                        m.clone().endOf('month').valueOf(),
                    ], this.selectedProjects as [string]);
                }),
            ),
        ]);
        this.checkInterrupted(jobId);

        // Build month labels from date range ('MMM YY' format)
        const months: Array<{
            label: string; year: number; month: number;
        }> = [];
        const mm = moment(range[0]).startOf('month');
        const endMm = moment(range[1]);
        while (mm.isSameOrBefore(endMm, 'month')) {
            months.push({
                label: mm.format('MMM YY'),
                year: mm.year(),
                month: mm.month() + 1,
            });
            mm.add(1, 'month');
        }

        // Build lookup: accType-year-month → total
        const crossTab: Record<string, number> = {};
        for (const row of crossResult) {
            const key = `${row.accType}-${Number(row.year)}`
                + `-${Number(row.month)}`;
            crossTab[key] = Number(row.total) || 0;
        }
        const getVal = (accType: string, m: { year: number; month: number }) =>
            crossTab[`${accType}-${m.year}-${m.month}`] || 0;

        this.generateLoadingText(0.7);

        // Build chart series
        const grossSales = {
            name: 'Gross Sales',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#404040' },
            smooth: true,
        };
        const cost = {
            name: 'Net COGS',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#63AED8' },
            smooth: true,
        };
        const grossProfit = {
            name: 'Gross Profit',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: GobiColor.COLOR1 },
            smooth: true,
        };
        const otherIncome = {
            name: 'Other Income',
            data: [] as number [],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#4FA89D' },
            smooth: true,
        };
        const expenses = {
            name: 'Expenses',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#F4DE96' },
            smooth: true,
        };
        const netProfit = {
            name: 'Net Profit',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: GobiColor.COLOR4 },
            smooth: true,
        };
        const forex = {
            name: 'Realised/Unrealised Forex',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#E89246' },
            smooth: true,
        };
        const npAfterEO = {
            name: 'NP after EO',
            data: [] as number[],
            type: 'bar',
            barWidth: '10%',
            barGap: '10%',
            itemStyle: { color: '#A2670D' },
            smooth: true,
        };

        const newArr: any[] = [];

        months.forEach((m, i) => {
            const sl = getVal('SL', m);
            const sa = getVal('SA', m);
            const co = getVal('CO', m);
            const ep = getVal('EP', m);
            const oi = getVal('OI', m);
            const eo = getVal('EO', m);
            const sm = isNaN(stockMovements[i]) ? 0 : stockMovements[i];

            const gs = sl * -1;
            const c = co + sm;
            const gp = (sl * -1 + sa * -1) - c;
            const oInc = oi * -1;
            const exp = ep;
            const np = gp + oInc - exp;
            const fx = eo * -1;
            const npeo = np + fx;

            grossSales.data.push(gs);
            cost.data.push(c);
            grossProfit.data.push(gp);
            otherIncome.data.push(oInc);
            expenses.data.push(exp);
            netProfit.data.push(np);
            forex.data.push(fx);
            npAfterEO.data.push(npeo);

            newArr.push({
                'month': m.label,
                'gross_sales': gs,
                'net_COGS': c,
                'g._p.': gp,
                'g._p.%': gs !== 0 ? (gp / gs) * 100 : 0,
                'other_income': oInc,
                'expenses': exp,
                'net_profit': np,
                'n._p.%': gs !== 0 ? (np / gs) * 100 : 0,
                'Realised_/_Unrealised_forex': fx,
                'np_after_eo': npeo,
                'n._p.%_*': gs !== 0 ? (npeo / gs * 100) : 0,
            });
        });

        this.xAxisData = months.map((m) => m.label);
        this.series = [grossSales, cost, grossProfit, otherIncome,
            expenses, forex, npAfterEO];

        this.tableFields = [
            { key: 'month', sortable: false },
            {
                key: 'gross_sales',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'net_COGS',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'g._p.',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'g._p.%',
                sortable: false,
                formatter: TableItemFormatter.percentage,
            },
            {
                key: 'other_income',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'expenses',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'net_profit',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'n._p.%',
                sortable: false,
                formatter: TableItemFormatter.percentage,
            },
            {
                key: 'Realised_/_Unrealised_forex',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'np_after_eo',
                sortable: false,
                formatter: TableItemFormatter.currencySplit,
            },
            {
                key: 'n._p.%_*',
                sortable: false,
                formatter: TableItemFormatter.percentage,
            },
        ];

        this.tableItems = newArr;

        this.generateLoadingText(1);

        this.saveHistory(
            'series',
            'series2',
            'xAxisData',
            'xAxisDate2',
            'tableFields',
            'tableItems',
        );
    }
  }

}
